'use client';

import { type DateTimeFormatOptions, useFormatter as useFormatterNextIntl } from 'next-intl';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { TimeZone } from '../../helpers/locale-helper';
/**
 * Custom hook that provides formatting functions using `next-intl`'s `useFormatter`.
 *
 * Add new format functions as needed.
 */
export function useFormat({ timeZone }: { timeZone: TimeZone } = { timeZone: 'Europe/Berlin' }) {
    const formatter = useFormatterNextIntl();
    const t = useTranslations();

    return useMemo(
        () => ({
            dateTime: {
                /**
                 * Formats a date to a readable time string (e.g., "14:30")
                 * @param date A date string or Date object
                 * @returns Formatted time string
                 */
                formatTime: (date: string | Date): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        hour: 'numeric',
                        minute: 'numeric'
                    }),

                /**
                 * Formats a date to a 24-hour time string (e.g., "14:30").
                 * @param date A date string or Date object to format.
                 * @returns A formatted time string in 24-hour format with two-digit hours and minutes (e.g., "14:30").
                 */
                formatDate_HHmm24: (date: string | Date): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    }),

                /**
                 * Formats a date to a human-readable string (e.g., "March 10, 2024").
                 * @param date A date string or Date object to format.
                 * @param options Additional Intl.DateTimeFormat options to customize the format.
                 * @returns A formatted date string with full month name (e.g., "March 10, 2024").
                 */
                formatDate: (date: string | Date, options?: DateTimeFormatOptions): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        ...options
                    }),

                /**
                 * Formats a date to a numeric string with 2-digit day and month (e.g., "10.03.2024").
                 * @param date A date string or Date object to format.
                 * @param options Additional Intl.DateTimeFormat options to customize the format.
                 * @returns A formatted date string with 2-digit day and month (e.g., "10.03.2024").
                 */
                formatDateNumeric: (date: string | Date, options?: DateTimeFormatOptions): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        ...options
                    }),

                /**
                 * Formats a date to a full date and time string (e.g., "March 10, 2024, 14:30").
                 * @param date A date string or Date object to format.
                 * @param options Optional Intl.DateTimeFormat options to customize the output.
                 * @returns A formatted string containing both date and time.
                 */
                formatDateAndTime: (date: string | Date, options?: DateTimeFormatOptions): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        year: 'numeric',
                        day: 'numeric',
                        month: 'long',
                        hour: '2-digit',
                        minute: '2-digit',
                        ...options
                    }),

                /**
                 * Formats a date and time with numeric date values (e.g., "10.03.2024, 14:30").
                 * @param date A date string or Date object to format.
                 * @param options Optional Intl.DateTimeFormat options to customize the output.
                 * @returns A formatted string with numeric date and time (e.g., "10.03.2024, 14:30").
                 */
                formatDateTimeNumeric: (date: string | Date, options?: DateTimeFormatOptions): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        year: 'numeric',
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        ...options
                    }),

                /**
                 * Formats a date using only numeric values (e.g., "10/03/2024" or "2024-03-10" depending on locale).
                 * @param date A date string or Date object to format.
                 * @param options Additional Intl.DateTimeFormat options to customize the format.
                 * @returns A formatted numeric date string (e.g., "03/10/2024" or "2024-03-10").
                 */
                formatDateNumbersOnly: (date: string | Date, options?: DateTimeFormatOptions): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        ...options
                    }),

                /**
                 * Formats a date to a short weekday format (e.g., "Mon", "Tue", "Wed")
                 * @param date A date string or Date object
                 * @param length "short" for 3-letter (Mon) or "narrow" for 2-letter (Mo)
                 * @returns Formatted weekday string
                 */
                formatWeekday: (date: string | Date, length: 'short' | 'narrow' = 'short'): string =>
                    formatter.dateTime(new Date(date), { timeZone: timeZone, weekday: length }),

                /**
                 * Formats a date to a short year format (e.g., "2024")
                 * @param date A date string or Date object
                 * @returns Formatted year string
                 */
                formatYear: (date: string | Date): string =>
                    formatter.dateTime(new Date(date), { timeZone: timeZone, year: 'numeric' }),

                /**
                 * Formats a date to be like "Sunday, April 24, 2022 at 7:00 PM"
                 * @param date A date string or Date object
                 * @returns Formatted date time string
                 */
                formatDateTimeLong: (date: string | Date): string =>
                    [
                        formatter.dateTime(new Date(date), {
                            timeZone: timeZone,
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            weekday: 'long',
                            month: 'long',
                            year: 'numeric'
                        }),
                        t('common.time.suffix')
                    ]
                        .filter((it) => it.length > 0)
                        .join(' '),

                /**
                 * Formats a date to day and month format (e.g., "31.12")
                 * @param date A date string or Date object
                 * @returns Formatted day and month string (e.g., "31.12")
                 */
                formatDayMonth: (date: string | Date): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        day: '2-digit',
                        month: '2-digit'
                    }),

                /**
                 * Formats a date to month and year format (e.g., "12.2024")
                 * @param date A date string or Date object
                 * @returns Formatted month and year string (e.g., "12.2024")
                 */
                formatYearMonth: (date: string | Date): string =>
                    formatter.dateTime(new Date(date), {
                        timeZone: timeZone,
                        month: '2-digit',
                        year: 'numeric'
                    }),

                /**
                 * Formats a date range as a string (e.g., "Mar 10 – 15, 2024" or "10.03. - 15.03.2024")
                 * @param startDate The start date of the range
                 * @param endDate The end date of the range
                 * @param options Optional Intl.DateTimeFormat options to customize the output
                 * @returns A formatted date range string
                 */
                formatDateTimeRange: (
                    startDate: string | Date,
                    endDate: string | Date,
                    options?: DateTimeFormatOptions
                ): string => formatter.dateTimeRange(new Date(startDate), new Date(endDate), options)
            },

            number: {
                /**
                 * Formats a number with thousands separators (e.g., "1,234,567")
                 * @param number The number to format
                 * @param fractionDigits Optional fixed decimal places (e.g., 1 for "2.3"); omit to keep
                 * the locale's default precision
                 * @returns Formatted number string
                 */
                formatNumber: (number: number, fractionDigits?: number): string =>
                    formatter.number(number, {
                        useGrouping: true,
                        ...(fractionDigits !== undefined && {
                            minimumFractionDigits: fractionDigits,
                            maximumFractionDigits: fractionDigits
                        })
                    }),

                /**
                 * Formats a number as a percentage (e.g., "50%" for 0.5 or "75%" for 0.75)
                 * @param number The decimal number to format (e.g., 0.5 for 50%)
                 * @param fractionDigits
                 * @returns Formatted percentage string (e.g., "50%")
                 */
                formatPercent: (number: number, fractionDigits = 2): string =>
                    formatter.number(number, { style: 'percent', maximumFractionDigits: fractionDigits }),

                /**
                 * Formats a number to a currency format (e.g., "$1,234.56" or "1.234,56 €")
                 * @param amount The numeric value to format
                 * @param currency The currency code (e.g., "USD", "EUR")
                 * @returns Formatted currency string
                 */
                formatCurrency: (amount: number, currency: string): string =>
                    formatter.number(amount, { style: 'currency', currency })
            }
        }),
        [formatter, t, timeZone]
    );
}
